import React, { Children, cloneElement, FC, useContext, useImperativeHandle, useMemo, useRef } from 'react';
import { usePopperTooltip } from 'react-popper-tooltip';
import { Portal } from '../../core/Portal';
import { Box } from '../Box';
import { Text } from '../Text';
import { Animation, ITransitionClasses } from '../../core/Animation';
import { classnames } from '@src/utils/classnames';
import { getClass } from '@src/utils/getClass';
import { AnyType } from '@src/models/common';
import styled from './TooltipNew.module.scss';
import { ITooltipProps } from './models';
import { AppContext } from '../../providers/App/AppContext';

/**
 * Новый, улучшенный тултип с рядом приемуществ:
 * - теперь новый тултип не оборачивает `children` в доп. `<div/>`
 * - `children` должны быть только в единственном числе
 * - `children` должен уметь принимать `ref` (т.е. обернут в `forwardRef` или его кастомная реализация)
 */
export const TooltipNew: FC<ITooltipProps> = ({
  children,
  placement = 'top',
  trigger,
  content,
  offset,
  interactive = true,
  view = 'basicBlack',
  className,
  style,
  maxWidth,
  open = true,
  visible: visibleProps,
  delayHide,
  delayShow,
  setTriggerRef: setTriggerRefProps,
}) => {
  const { mode } = useContext(AppContext);

  const { visible, setTriggerRef, setTooltipRef, getTooltipProps, getArrowProps } = usePopperTooltip({
    trigger,
    placement,
    offset,
    interactive,
    delayHide,
    delayShow,
    visible: visibleProps,
  });

  const prevVisible = useRef(visible);

  useImperativeHandle(setTriggerRefProps, () => setTriggerRef, [setTriggerRef]);

  const newChildren = useMemo(() => {
    try {
      if (Children.count(children) > 1) {
        throw new Error("'Children для тултипа не может быть массивом. Пожалуйста передайте корректное значение'");
      }

      /** Если мы передаем ссылку на якорь сверху, но оставляем детей как есть */
      if (setTriggerRefProps) return children;

      return cloneElement(children as AnyType, {
        ref: setTriggerRef,
      });
    } catch (error) {
      console.error('Произошла ошибка при попытке навесить тултип на элемент.', error, children);
      return children;
    }
  }, [children, setTriggerRef, setTriggerRefProps]);

  const transitionClasses: ITransitionClasses = {
    appear: styled.rootEnter,
    appearActive: styled.rootEnterActive,
    enter: styled.rootEnter,
    enterActive: styled.rootEnterActive,
    exit: styled.rootExit,
    exitActive: styled.rootExitActive,
  };

  const classes = 'Tooltip__root ' + classnames(className, getClass(styled, 'view', view));

  let currentVisible = visible;

  // в dev mode в момент когда нужно закрыть тултип,
  // мы будем отменять это значение на то,
  // что указано в `window.__CENTRA__.AUTO_TOOLTIP_CLOSE`
  if (typeof window !== 'undefined' && mode === 'dev' && typeof window.__CENTRA__?.AUTO_TOOLTIP_CLOSE === 'boolean') {
    if (prevVisible.current && !visible) {
      currentVisible = !window.__CENTRA__.AUTO_TOOLTIP_CLOSE;
    }

    prevVisible.current = visible;
  }

  return (
    <>
      {newChildren}
      {content && (
        <Portal>
          <Animation open={open && currentVisible} mountOnEnter unmountOnExit transitionClasses={transitionClasses}>
            <div tabIndex={0} ref={setTooltipRef} {...getTooltipProps({ className: classes, style })}>
              <div style={{ position: 'relative' }}>
                <div {...(view === 'default' ? {} : getArrowProps({ className: styled.arrow }))} />
                {typeof content === 'string' ? (
                  <Box style={{ maxWidth }}>
                    <Text id={'tooltipContent'} size="caption">
                      {content}
                    </Text>
                  </Box>
                ) : (
                  content
                )}
              </div>
            </div>
          </Animation>
        </Portal>
      )}
    </>
  );
};
